// Portal Support (Jill 2026-10-20): what to do when the portal is down, how
// to update, this device's version and sync state, and Report a problem.
//
// This page has to work when nothing else does, so:
//   - it is a PUBLIC route (no login): when the login service is the part
//     that is down, a pilot can still read it, and the login page links here;
//   - it is imported eagerly in App.jsx, not lazy, so it lives in the main
//     bundle every installed copy already holds (a lazy chunk is exactly what
//     fails to load right after an update or a cache eviction);
//   - it renders its own small header, so it never waits on the nav, roles,
//     or any request. Nothing on it fetches.
// Contact is Report a problem only (Jill 2026-10-20): no phone number here,
// since logged-out visitors see this page too.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, PageHeader, Button } from "../ui";
import theme from "../theme";
import seapaLogo from "../assets/SeapaLogo.png";
import { useAuth } from "../contexts/AuthContext";
import { computeSyncSummary } from "../lib/syncSummary";
import { paperBlanks } from "../lib/paperBlanks";

const T = theme.tokens;

function Section({ title, children }) {
  return (
    <Card style={{ padding: "16px 18px", marginBottom: 14 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: T.text.lg, color: T.ink, fontWeight: 700 }}>{title}</h2>
      <div style={{ color: T.textMuted, fontSize: T.text.md, lineHeight: 1.55 }}>{children}</div>
    </Card>
  );
}

const list = { margin: "6px 0 0", paddingLeft: 20 };

export default function PortalSupport() {
  const { session } = useAuth();
  const [sync, setSync] = useState(computeSyncSummary);
  useEffect(() => {
    const update = () => setSync(computeSyncSummary());
    window.addEventListener("submit-queue-changed", update);
    window.addEventListener("online", update);
    const iv = setInterval(update, 20000);
    return () => {
      window.removeEventListener("submit-queue-changed", update);
      window.removeEventListener("online", update);
      clearInterval(iv);
    };
  }, []);
  const version = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

  return (
    <div style={{ minHeight: "100vh", background: "var(--page)" }}>
      <header style={{
        display: "flex", alignItems: "center", gap: 10, background: T.navBg, color: T.navFg,
        padding: "8px 14px", boxShadow: theme.shadows.navbar,
      }}>
        <img src={seapaLogo} alt="SEAPA Logo" style={{ height: 32 }} />
        <span style={{ fontWeight: 600, fontSize: "1rem", whiteSpace: "nowrap" }}>SEAPA Portal</span>
        <Link to={session ? "/" : "/login"} style={{ marginLeft: "auto", color: T.navFg, fontWeight: 600, fontSize: T.text.sm, textDecoration: "none" }}>
          {session ? "Back to the portal" : "Back to sign in"}
        </Link>
      </header>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "18px 16px 40px", textAlign: "left" }}>
        <PageHeader
          title="Portal Support"
          subtitle="If the portal will not open or will not send, nothing you have typed is lost. This page opens even with no signal."
        />

        <Section title="If the portal is down">
          <ul style={list}>
            <li>
              <strong>Slips and expense reports.</strong> The office does not get a copy from the portal.
              Submit to the office yourself as usual, so nothing is late because the portal is down.
              Your portal draft stays on this phone; send it when the portal is back.
            </li>
            <li>
              <strong>Training records.</strong> Keep the draft. If one is due, fill in a printed TR
              and sign it as usual; it can be entered in the portal afterwards.
            </li>
            <li>
              <strong>Cargo and yacht sheets.</strong> The blank sheets open from here with no signal:{" "}
              {paperBlanks().map((b, i) => (
                <span key={b.href}>
                  {i > 0 && " · "}
                  <a href={b.href} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontWeight: 600 }}>{b.label}</a>
                </span>
              ))}
            </li>
            <li>
              <strong>Dispatch and the voyage plan.</strong> Screens you opened recently still show their last copy.
            </li>
          </ul>
        </Section>

        <Section title="Keep what is on your phone">
          <ul style={list}>
            <li>Do <strong>not</strong> delete the SEAPA Portal icon from your home screen.</li>
            <li>Do <strong>not</strong> clear Safari history or website data.</li>
            <li>If your phone says its storage is full, use Report a problem before deleting anything. A full phone is the one thing that has erased drafts.</li>
          </ul>
        </Section>

        <Section title="Updating the portal">
          When &ldquo;An update to the portal is ready&rdquo; appears, tap <strong>Reload</strong>. That is the whole update.
        </Section>

        <Section title="This device">
          <div>Version <strong style={{ color: T.ink }}>{version}</strong></div>
          <div style={{ marginTop: 4, color: sync.waiting ? "var(--warn-fg)" : "var(--success-fg)", fontWeight: 600 }}>{sync.text}</div>
          <div style={{ marginTop: 6, fontSize: T.text.sm }}>Include these two lines if you text someone about a problem.</div>
        </Section>

        <Section title="Report a problem">
          {session ? (
            <>
              <div style={{ marginBottom: 10 }}>
                Sends Jill what you saw, with the page and version filled in. Add a screenshot if you can. It needs a signal.
              </div>
              <Button onClick={() => window.dispatchEvent(new Event("seapa:open-feedback"))}>Report a problem</Button>
            </>
          ) : (
            <div>Report a problem needs you signed in. Once you can sign in, it is here and in the account menu as Send feedback.</div>
          )}
        </Section>
      </div>
    </div>
  );
}
